import { OrgSuggestion } from '../../model/types';

export type OrganizationRegistrationStage = 'lookup' | 'review';

export type OnChangeOrganizationRegistrationField = <K extends keyof OrganizationRegistrationFormState>(
  key: K,
  value: OrganizationRegistrationFormState[K]
) => void;

export interface OrganizationRegistrationTouched {
  address: boolean;
  addressEn: boolean;
  fullName: boolean;
  fullNameEn: boolean;
  kpp: boolean;
  ogrn: boolean;
  phone: boolean;
  shortName: boolean;
}

export type OrganizationRegistrationModifiedFields = Partial<
  Record<keyof OrganizationRegistrationTouched, boolean>
>;

export interface OrganizationInnDraft {
  form: OrganizationRegistrationFormState;
  modifiedFields: OrganizationRegistrationModifiedFields;
  touched: OrganizationRegistrationTouched;
}

export interface OrgRegistrationLocationState {
  draft?: OrganizationRegistrationDraft;
  returnTo?: string;
}

export interface OrganizationRegistrationFormState {
  address: string;
  addressCompleteError?: string;
  addressEn: string;
  addressFiasId: string;
  fullName: string;
  fullNameEn: string;
  inn: string;
  isAddressSelected: boolean;
  kpp: string;
  ogrn: string;
  phone: string;
  shortName: string;
}

export interface OrganizationRegistrationValidation {
  addressEnError: string;
  addressError: string;
  areFormFieldsValid: boolean;
  fullNameEnError: string;
  fullNameError: string;
  isIndividualEntrepreneur: boolean;
  kppError: string;
  ogrnError: string;
  phoneError: string;
  shortNameError: string;
}

export interface OrganizationRegistrationDraft {
  form: OrganizationRegistrationFormState;
  innInput: string;
  isInnTouched: boolean;
  isOfferAccepted: boolean;
  modifiedFields?: OrganizationRegistrationModifiedFields;
  stage: OrganizationRegistrationStage;
  submitAttempted: boolean;
  touched: OrganizationRegistrationTouched;
}

export const emptyOrganizationRegistrationForm: OrganizationRegistrationFormState = {
  address: '',
  addressCompleteError: '',
  addressEn: '',
  addressFiasId: '',
  fullName: '',
  fullNameEn: '',
  inn: '',
  isAddressSelected: false,
  kpp: '',
  ogrn: '',
  phone: '',
  shortName: ''
};

export const suggestionToOrganizationRegistrationForm = (
  suggestion: OrgSuggestion,
  existingDraft?: OrganizationInnDraft | null
): OrganizationRegistrationFormState => {
  const suggestionRecord = suggestion as unknown as Record<string, unknown>;
  const inn = (suggestion.inn ?? existingDraft?.form.inn ?? '').trim();
  const isIndividualEntrepreneur = inn.length === 12;

  const baseForm: OrganizationRegistrationFormState = {
    address: isIndividualEntrepreneur ? '' : (suggestion.address ?? ''),
    addressCompleteError: '',
    addressEn: isIndividualEntrepreneur
      ? ''
      : ((typeof suggestion.addressEn === 'string' ? suggestion.addressEn : null)
        ?? (typeof suggestionRecord.address_en === 'string' ? suggestionRecord.address_en : '')
        ?? ''),
    addressFiasId: '',
    fullName: suggestion.fullName ?? '',
    fullNameEn: (typeof suggestion.fullNameEn === 'string' ? suggestion.fullNameEn : null)
      ?? (typeof suggestionRecord.full_name_en === 'string' ? suggestionRecord.full_name_en : '')
      ?? '',
    inn: suggestion.inn ?? '',
    isAddressSelected: !isIndividualEntrepreneur,
    kpp: suggestion.kpp ?? '',
    ogrn: suggestion.ogrn ?? '',
    phone: suggestion.phone ?? '',
    shortName: suggestion.shortName ?? ''
  };

  if (!existingDraft) {
    return baseForm;
  }

  const { form: draftForm, modifiedFields } = existingDraft;

  return {
    address: isIndividualEntrepreneur
      ? (draftForm.address ?? '')
      : (modifiedFields?.address ? draftForm.address : baseForm.address),
    addressCompleteError: isIndividualEntrepreneur
      ? (draftForm.addressCompleteError ?? '')
      : '',
    addressEn: isIndividualEntrepreneur
      ? (draftForm.addressEn ?? '')
      : (modifiedFields?.addressEn ? draftForm.addressEn : baseForm.addressEn),
    addressFiasId: isIndividualEntrepreneur
      ? (draftForm.addressFiasId ?? '')
      : '',
    fullName: modifiedFields?.fullName ? draftForm.fullName : baseForm.fullName,
    fullNameEn: modifiedFields?.fullNameEn ? draftForm.fullNameEn : baseForm.fullNameEn,
    inn: baseForm.inn,
    isAddressSelected: isIndividualEntrepreneur
      ? Boolean(draftForm.isAddressSelected)
      : true,
    kpp: isIndividualEntrepreneur ? '' : (modifiedFields?.kpp ? draftForm.kpp : baseForm.kpp),
    ogrn: modifiedFields?.ogrn ? draftForm.ogrn : baseForm.ogrn,
    phone: modifiedFields?.phone ? draftForm.phone : baseForm.phone,
    shortName: modifiedFields?.shortName ? draftForm.shortName : baseForm.shortName
  };
};